/**
 * Tiny fetch wrapper for talking to the platform API on the ERP
 * backend. Always sends credentials so the platform_session cookie
 * round-trips, and surfaces non-2xx responses as thrown errors with
 * the server's JSON message attached.
 *
 * In dev the backend is `http://localhost:8787`. In prod, point
 * VITE_BACKEND_URL at your real API host (e.g. https://api.example.cloud).
 * Marketing never talks to anything else — every fetch from the
 * marketing app should go through this module.
 */

const RAW_BASE = import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:8787'
// Strip a trailing slash so callers can write 'platform/me' without
// caring whether the env var ends in '/'.
const BASE = RAW_BASE.replace(/\/+$/, '')

export class PlatformApiError extends Error {
  status: number
  body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'PlatformApiError'
    this.status = status
    this.body = body
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

export async function platformApi<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers ?? {}),
    },
    signal: options.signal,
  }
  if (options.body !== undefined) {
    init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body)
  }

  const res = await fetch(url, init)
  // Treat 204 as success-with-no-body; otherwise expect JSON.
  if (res.status === 204) return undefined as T

  const contentType = res.headers.get('content-type') ?? ''
  let parsed: unknown
  if (contentType.includes('application/json')) {
    try {
      parsed = await res.json()
    } catch {
      parsed = null
    }
  } else {
    parsed = await res.text()
  }

  if (!res.ok) {
    const message =
      (typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : null) ??
      (typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ??
      `Request failed with status ${res.status}`
    throw new PlatformApiError(message, res.status, parsed)
  }

  return parsed as T
}

// ---------- Typed helpers for the platform auth surface ----------

export interface PlatformUser {
  id: string
  email: string
  name: string | null
  image: string | null
  role: string | null
}

export async function getCurrentUser(signal?: AbortSignal): Promise<PlatformUser> {
  const data = await platformApi<{ user: PlatformUser }>('/api/platform/me', { signal })
  return data.user
}

/**
 * One workspace the current platform user belongs to. Comes from
 * tenant_members ⨝ tenants. The backend never returns the
 * db_connection columns here — those are not just hidden, they're
 * column-level REVOKE'd from the marketing_rw role too.
 */
/**
 * All statuses the marketing UI might render. `deleted` should
 * never reach the client — `/api/platform/tenants` filters it out
 * — but the union has it anyway so an out-of-band admin action is
 * still type-safe.
 */
export type TenantStatus = 'active' | 'paused' | 'pending_deletion' | 'suspended' | 'deleted' | null

export interface PlatformTenant {
  id: string
  name: string
  slug: string
  status: TenantStatus
  tier: string
  emailDomain: string | null
  createdAt: string
  /** ISO timestamp set only when status is 'pending_deletion'. */
  scheduledDeleteAt: string | null
  /** The current user's role in this tenant. For now always 'owner'. */
  role: string
  joinedAt: string
}

export async function getMyTenants(signal?: AbortSignal): Promise<PlatformTenant[]> {
  const data = await platformApi<{ tenants: PlatformTenant[] }>('/api/platform/tenants', {
    signal,
  })
  return data.tenants
}

/**
 * One row in the tenant member list — a platform user who has a
 * `tenant_members` link to the workspace. `joinedAt` is the ISO
 * timestamp of the link row, not when the platform user account
 * itself was created.
 */
export interface TenantMember {
  userId: string
  email: string
  name: string | null
  role: string
  joinedAt: string
}

export async function getTenantMembers(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantMember[]> {
  const data = await platformApi<{ members: TenantMember[] }>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/members`,
    { signal },
  )
  return data.members
}

/**
 * One pending invite row as returned by the tenant detail list.
 * Invites that have been accepted become tenant_members and move
 * out of this list into the members list.
 */
export interface TenantInvite {
  id: string
  email: string
  role: string
  invitedByEmail: string | null
  expiresAt: string
  createdAt: string
}

/**
 * Discriminated result for the add-member endpoint:
 *
 *   - `status='joined'` — the target email matched an existing
 *     platform user and they were added to tenant_members
 *     immediately. The `member` shape is the same as before.
 *
 *   - `status='invited'` — the target email is new to the
 *     platform. We created a pending invite row and return the
 *     copyable URL here so the UI can render a "send this link"
 *     fallback. The backend ALSO attempts email delivery (stubbed
 *     to stdout in MVP), so callers can optionally display both.
 */
export type AddMemberResult =
  | { status: 'joined'; member: TenantMember }
  | {
      status: 'invited'
      invite: {
        id: string
        email: string
        role: string
        expiresAt: string
        createdAt: string
        inviteUrl: string
      }
    }

/**
 * Add someone to a tenant by email. Two paths:
 *
 *   - Existing platform user → joined immediately.
 *   - New email → a 7-day invite is created. The returned
 *     `inviteUrl` is the copyable accept link; the backend ALSO
 *     logs the invite to stdout (real email provider plugs in
 *     later), so callers can treat the URL as a fallback channel.
 *
 * Returns 409 `AlreadyMember` if the email is already on the team.
 */
export async function addTenantMember(
  tenantId: string,
  email: string,
  role: 'owner' | 'admin' | 'billing' | 'viewer' = 'owner',
): Promise<AddMemberResult> {
  return platformApi<AddMemberResult>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/members`,
    {
      method: 'POST',
      body: { email, role },
    },
  )
}

export async function removeTenantMember(tenantId: string, userId: string): Promise<void> {
  await platformApi<void>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  )
}

/**
 * List pending invites for a tenant. Accepted invites are gone
 * from this list — they become tenant_members and show up in
 * getTenantMembers. Expired invites still appear so the member
 * can explicitly revoke them.
 */
export async function getTenantInvites(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantInvite[]> {
  const data = await platformApi<{ invites: TenantInvite[] }>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/invites`,
    { signal },
  )
  return data.invites
}

/** Revoke a pending invite. Hard-deletes the row. */
export async function revokeTenantInvite(tenantId: string, inviteId: string): Promise<void> {
  await platformApi<void>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(inviteId)}`,
    { method: 'DELETE' },
  )
}

/**
 * Public (no-auth) lookup for an invite token. Returns minimal
 * display metadata for the accept-invite signup page — email,
 * tenant name, inviter — and carefully omits tenant id / db URL
 * / internal ids to avoid leaking platform structure to random
 * token holders.
 *
 * Returns 404 for unknown tokens, 410 Gone for expired or
 * already-accepted tokens. Callers should surface the server
 * message verbatim since the backend picks friendlier copy than
 * we can guess here.
 */
export interface InviteLookup {
  email: string
  role: string
  tenantName: string
  invitedByEmail: string | null
  expiresAt: string
}

export async function lookupInvite(token: string, signal?: AbortSignal): Promise<InviteLookup> {
  const data = await platformApi<{ invite: InviteLookup }>(
    `/api/platform/invites/lookup?token=${encodeURIComponent(token)}`,
    { signal },
  )
  return data.invite
}

/**
 * Accept an invite by token. Creates the platform_user via
 * better-auth (password hashing, session cookies, etc.) and
 * inserts the tenant_members row in a single backend transaction.
 * On success the response's Set-Cookie headers already carry a
 * valid platform session, so the caller can immediately navigate
 * to /account without a second sign-in round-trip.
 */
export async function acceptInvite(params: {
  token: string
  password: string
  name: string
}): Promise<void> {
  await platformApi<{ ok: true }>('/api/platform/invites/accept', {
    method: 'POST',
    body: params,
  })
}

/**
 * Rename a workspace. Server enforces a ≤100 character limit and
 * a uniqueness constraint — duplicate names return 409 `Conflict`
 * with a human-readable message. Slug is immutable.
 */
export async function renameTenant(tenantId: string, name: string): Promise<PlatformTenant> {
  const data = await platformApi<{ tenant: PlatformTenant }>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}`,
    {
      method: 'PATCH',
      body: { name },
    },
  )
  return data.tenant
}

/**
 * Schedule a workspace for deletion. Transitions the tenant to
 * `pending_deletion` with a 30-day grace window, during which:
 *   - users can still sign in
 *   - reads still work
 *   - writes are blocked by the backend (423 Locked)
 *
 * The workspace stays visible on /account with a countdown and
 * "Cancel deletion" action. The underlying Postgres DB is preserved
 * until a future hard-delete cron runs. Returns the updated shape
 * so the caller can swap local state without a second fetch.
 */
export async function scheduleTenantDeletion(tenantId: string): Promise<{
  id: string
  status: TenantStatus
  scheduledDeleteAt: string | null
}> {
  const data = await platformApi<{
    tenant: { id: string; status: TenantStatus; scheduledDeleteAt: string | null }
  }>(`/api/platform/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'DELETE',
  })
  return data.tenant
}

/**
 * Pause a workspace — read-only, indefinitely, reversible. Only
 * valid from `active`. Returns 409 `InvalidState` otherwise.
 */
export async function pauseTenant(tenantId: string): Promise<{ id: string; status: TenantStatus }> {
  const data = await platformApi<{ tenant: { id: string; status: TenantStatus } }>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/pause`,
    { method: 'POST' },
  )
  return data.tenant
}

/**
 * Resume a paused workspace. Only valid from `paused` — does NOT
 * cancel a pending deletion (use cancelTenantDeletion for that).
 */
export async function resumeTenant(
  tenantId: string,
): Promise<{ id: string; status: TenantStatus }> {
  const data = await platformApi<{ tenant: { id: string; status: TenantStatus } }>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/resume`,
    { method: 'POST' },
  )
  return data.tenant
}

/**
 * Cancel a scheduled deletion and return the workspace to
 * `active`. Clears `scheduledDeleteAt`. Only valid from
 * `pending_deletion`.
 */
export async function cancelTenantDeletion(tenantId: string): Promise<{
  id: string
  status: TenantStatus
  scheduledDeleteAt: string | null
}> {
  const data = await platformApi<{
    tenant: { id: string; status: TenantStatus; scheduledDeleteAt: string | null }
  }>(`/api/platform/tenants/${encodeURIComponent(tenantId)}/cancel-deletion`, {
    method: 'POST',
  })
  return data.tenant
}

// ---------- Admin lifecycle override ----------
//
// These mirror pauseTenant / resumeTenant / scheduleTenantDeletion /
// cancelTenantDeletion but hit the `/admin/tenants/:id/...`
// endpoints, which bypass the membership gate. Platform admins use
// them to help customers who can't reach their own /account —
// account recovery, support escalations, compliance pauses.
//
// The response shapes are identical to the owner-facing helpers
// so the caller doesn't have to branch on "did we use the admin
// route or the owner route".

export async function adminPauseTenant(
  tenantId: string,
): Promise<{ id: string; status: TenantStatus }> {
  const data = await platformApi<{ tenant: { id: string; status: TenantStatus } }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/pause`,
    { method: 'POST' },
  )
  return data.tenant
}

export async function adminResumeTenant(
  tenantId: string,
): Promise<{ id: string; status: TenantStatus }> {
  const data = await platformApi<{ tenant: { id: string; status: TenantStatus } }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/resume`,
    { method: 'POST' },
  )
  return data.tenant
}

export async function adminScheduleTenantDeletion(tenantId: string): Promise<{
  id: string
  status: TenantStatus
  scheduledDeleteAt: string | null
}> {
  const data = await platformApi<{
    tenant: { id: string; status: TenantStatus; scheduledDeleteAt: string | null }
  }>(`/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/schedule-deletion`, {
    method: 'POST',
  })
  return data.tenant
}

export async function adminCancelTenantDeletion(tenantId: string): Promise<{
  id: string
  status: TenantStatus
  scheduledDeleteAt: string | null
}> {
  const data = await platformApi<{
    tenant: { id: string; status: TenantStatus; scheduledDeleteAt: string | null }
  }>(`/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/cancel-deletion`, {
    method: 'POST',
  })
  return data.tenant
}

export interface ForceDeleteResult {
  deleted: boolean
  tenantId: string
  name: string
}

/**
 * Immediately destroy a tenant — drops the database, role, and all
 * control DB rows. Requires the caller to pass `confirm` set to the
 * tenant's exact name to guard against accidental clicks. Only
 * platform_admin may call this (platform_support gets 403).
 */
export async function adminForceDeleteTenant(
  tenantId: string,
  confirmName: string,
): Promise<ForceDeleteResult> {
  return platformApi<ForceDeleteResult>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/force-delete`,
    {
      method: 'POST',
      body: JSON.stringify({ confirm: confirmName }),
    },
  )
}

// ---------- Self-service workspace creation ----------

export interface CreateWorkspaceResult {
  tenant: {
    id: string
    name: string
    slug: string
    status: string
  }
}

/**
 * Create a new workspace for the current user. Does not require admin
 * role — any authenticated platform user can create workspaces up to
 * a per-account limit. The backend provisions the DB, seeds the
 * creator as admin, and registers them in the cross-tenant user index.
 */
export async function createWorkspace(name: string): Promise<CreateWorkspaceResult> {
  return platformApi<CreateWorkspaceResult>('/api/platform/tenants/create', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

// ---------- Stripe payments ----------

export async function getStripeConfig(): Promise<{ publishableKey: string }> {
  return platformApi<{ publishableKey: string }>('/api/platform/stripe/config')
}

export async function createCheckoutSession(
  tier: string,
  workspaceName: string,
): Promise<{ url: string }> {
  return platformApi<{ url: string }>('/api/platform/stripe/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ tier, workspaceName }),
  })
}

export async function createPortalSession(): Promise<{ url: string }> {
  return platformApi<{ url: string }>('/api/platform/stripe/create-portal-session', {
    method: 'POST',
  })
}

// ---------- Admin surface ----------

/**
 * One member of a tenant as returned by the admin tenant-list
 * endpoint. Distinct from a tenant USER (which lives in the
 * per-tenant DB) — this is the platform-side record of who can
 * manage the workspace from the marketing dashboard.
 */
export interface AdminTenantMember {
  userId: string
  email: string
  name: string | null
  role: string
}

/**
 * One tenant row as returned by GET /api/platform/admin/tenants.
 * Includes the aggregated member list, since that's what the
 * admin table needs to render "owner: dominic@beyond.com" per row.
 *
 * No `db_connection` / `db_connection_encrypted` fields — admin
 * endpoints intentionally never expose those so a compromised
 * admin session cannot leak tenant URLs.
 */
export interface AdminTenant {
  id: string
  name: string
  slug: string
  status: string | null
  tier: string
  emailDomain: string | null
  createdAt: string
  updatedAt: string | null
  members: AdminTenantMember[]
  moduleCount: number
}

/**
 * Admin-only: fetch every tenant in the platform. Requires the
 * current platform session to have role in {platform_admin,
 * platform_support, platform_readonly}. 403 otherwise.
 */
export async function getAdminTenants(signal?: AbortSignal): Promise<AdminTenant[]> {
  const data = await platformApi<{ tenants: AdminTenant[] }>('/api/platform/admin/tenants', {
    signal,
  })
  return data.tenants
}

export interface OverviewAuditEvent {
  id: string
  occurredAt: string
  actorEmail: string | null
  action: string
  targetKind: string | null
  targetId: string | null
  targetLabel: string | null
  status: string
}

export interface PlatformOverview {
  totals: {
    tenants: number
    byStatus: Record<string, number>
    byTier: Record<string, number>
  }
  signups: Array<{ weekStart: string; count: number }>
  attention: {
    pendingDeletion: Array<{ id: string; name: string; scheduledDeleteAt: string | null }>
    paused: Array<{ id: string; name: string; updatedAt: string | null }>
    expiringLicenses: Array<{
      tenantId: string
      tenantName: string
      moduleKey: string
      expiresAt: string
    }>
    unverifiedDomains: Array<{ id: string; name: string; emailDomain: string | null }>
  }
  recentEvents: OverviewAuditEvent[]
}

export async function getAdminOverview(signal?: AbortSignal): Promise<PlatformOverview> {
  return platformApi<PlatformOverview>('/api/platform/admin/overview', { signal })
}

export interface CreateTenantInput {
  name: string
  emailDomain: string
  tier: 'personal' | 'business' | 'enterprise'
}

/**
 * Admin-write: provision a new tenant. Returns the created tenant
 * in the same shape getAdminTenants() items use, so the caller can
 * navigate to the list and see it immediately.
 *
 * Backend restricts this to platform_admin + platform_support.
 * platform_readonly gets 403.
 */
export async function createAdminTenant(input: CreateTenantInput): Promise<AdminTenant> {
  const data = await platformApi<{ tenant: AdminTenant }>('/api/platform/admin/tenants', {
    method: 'POST',
    body: input,
  })
  return data.tenant
}

/**
 * A single tenant_licenses row joined with module_catalog display
 * metadata. Used by the admin tenant detail page to render the
 * license table.
 *
 * `moduleName` / `moduleDescription` / `requiresLicense` come from
 * module_catalog via LEFT JOIN — they're nullable because a stale
 * license row referencing a deleted module should still appear so
 * an admin can revoke it.
 */
export interface AdminLicense {
  id: string
  moduleKey: string
  status: string
  grantedAt: string
  expiresAt: string | null
  notes: string | null
  moduleName: string | null
  moduleDescription: string | null
  requiresLicense: boolean | null
}

/** One tenant_modules row — the per-tenant module activation state. */
export interface AdminTenantModule {
  moduleKey: string
  status: string
  activatedAt: string | null
  deactivatedAt: string | null
}

/**
 * Tenant detail — everything the admin tenant page needs to render.
 * Superset of AdminTenant with members, licenses, and modules.
 */
export interface AdminTenantDetail {
  id: string
  name: string
  slug: string
  status: TenantStatus
  tier: string
  emailDomain: string | null
  emailDomainVerifiedAt: string | null
  createdAt: string
  updatedAt: string | null
  /** Set only when status is 'pending_deletion'. */
  scheduledDeleteAt: string | null
  members: AdminTenantMember[]
  licenses: AdminLicense[]
  modules: AdminTenantModule[]
}

/**
 * Admin-read: fetch one tenant by id. Backend returns 404 when the
 * id doesn't resolve; the helper throws a PlatformApiError with
 * status 404 in that case.
 */
export async function getAdminTenant(id: string, signal?: AbortSignal): Promise<AdminTenantDetail> {
  const data = await platformApi<{ tenant: AdminTenantDetail }>(
    `/api/platform/admin/tenants/${encodeURIComponent(id)}`,
    { signal },
  )
  return data.tenant
}

/**
 * A module_catalog entry as returned by GET /admin/modules. Used
 * by the grant-license picker to populate the dropdown of
 * available modules.
 */
export interface AdminModule {
  key: string
  name: string
  description: string | null
  isCore: boolean
  source: string
  requiresLicense: boolean
  monthlyPriceCents: string | null
}

export async function getAdminModules(signal?: AbortSignal): Promise<AdminModule[]> {
  const data = await platformApi<{ modules: AdminModule[] }>('/api/platform/admin/modules', {
    signal,
  })
  return data.modules
}

export interface UpdateModuleInput {
  name?: string
  description?: string | null
  source?: 'builtin' | 'marketplace' | 'custom_tenant'
  requiresLicense?: boolean
  monthlyPriceCents?: string | null
  metadata?: Record<string, unknown>
}

/**
 * Admin-write: patch a module_catalog row. Only the provided fields
 * are updated; the server leaves everything else alone. Returns the
 * full updated row so the caller can swap it into local state
 * without a second fetch.
 *
 * The most important field here is `requiresLicense` — flipping it
 * to true activates the Path B license filter for that module,
 * causing loadTenantModules to skip it for any tenant without an
 * active tenant_licenses row.
 */
export async function updateAdminModule(
  key: string,
  input: UpdateModuleInput,
): Promise<AdminModule> {
  const data = await platformApi<{ module: AdminModule }>(
    `/api/platform/admin/modules/${encodeURIComponent(key)}`,
    {
      method: 'PATCH',
      body: input,
    },
  )
  return data.module
}

export interface GrantLicenseInput {
  tenantId: string
  moduleKey: string
  status?: 'active' | 'trial' | 'expired' | 'revoked'
  expiresAt?: string | null
  notes?: string | null
}

/**
 * Admin-write: grant (or re-grant) a license. Server upserts on
 * (tenant_id, module_key) so calling this for an existing license
 * just flips status / notes / expiresAt.
 */
export async function grantTenantLicense(input: GrantLicenseInput): Promise<void> {
  await platformApi<{ ok: true }>(
    `/api/platform/admin/tenants/${encodeURIComponent(input.tenantId)}/licenses`,
    {
      method: 'POST',
      body: {
        moduleKey: input.moduleKey,
        status: input.status ?? 'active',
        expiresAt: input.expiresAt ?? null,
        notes: input.notes ?? null,
      },
    },
  )
}

/**
 * Admin-write: hard-delete a license. Revoking a module the tenant
 * doesn't have is a no-op (the DELETE matches zero rows).
 */
export async function revokeTenantLicense(tenantId: string, moduleKey: string): Promise<void> {
  await platformApi<void>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/licenses/${encodeURIComponent(moduleKey)}`,
    { method: 'DELETE' },
  )
}

// ---------- Module install / uninstall ----------
//
// Operator-facing actions for "enable this TS module on this
// tenant" and "disable it". Distinct from license grant/revoke:
//
//   - Install writes a `tenant_modules` row (the runtime filter
//     in loadTenantModules checks this). If the module requires
//     a license and none exists, the backend auto-grants one.
//
//   - Uninstall hard-deletes the tenant_modules row but leaves
//     the license alone — revoking billing is a separate concern
//     with its own endpoint.

export interface InstallTenantModuleResult {
  module: { moduleKey: string; status: string; activatedAt: string }
  /** true if the install also created a fresh license row. */
  licenseGranted: boolean
  /**
   * Number of workflows that were previously auto-paused (because
   * this module was missing) and have now been re-enabled by the
   * cascade. Lets the operator see "5 workflows are running again"
   * instead of having to click into the tenant's ERP to notice the
   * change.
   */
  workflowsReEnabled?: number
}

export async function installTenantModule(
  tenantId: string,
  moduleKey: string,
): Promise<InstallTenantModuleResult> {
  return platformApi<InstallTenantModuleResult>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/modules/${encodeURIComponent(moduleKey)}/install`,
    { method: 'POST' },
  )
}

export interface UninstallTenantModuleResult {
  /**
   * Number of workflows that the cascade auto-paused as a result
   * of this uninstall. The disable_reason on each row points back
   * to this module so the tenant sees what got broken.
   */
  workflowsDisabled?: number
}

export async function uninstallTenantModule(
  tenantId: string,
  moduleKey: string,
): Promise<UninstallTenantModuleResult> {
  return platformApi<UninstallTenantModuleResult>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/modules/${encodeURIComponent(moduleKey)}`,
    { method: 'DELETE' },
  )
}

export interface SignUpInput {
  email: string
  password: string
  name?: string
  /**
   * URL to land on after the verification link is clicked. Passed
   * into better-auth which embeds it in the verification email.
   * Clicking the email link triggers a redirect to
   * `${callbackURL}?error=CODE` on failure, or plain `${callbackURL}`
   * on success. The marketing sign-up flow sets this to the /account
   * origin so verified users arrive signed in.
   */
  callbackURL?: string
}

/**
 * Result shape when `requireEmailVerification: true` on the backend.
 * The sign-up response carries `token: null` because better-auth
 * skips auto-signin until verification completes. Older code that
 * assumed a session was attached on signup needs to check for
 * `token === null` and transition to a "check your email" state.
 */
export interface SignUpResult {
  token: string | null
  user: {
    id: string
    email: string
    name: string | null
    emailVerified: boolean
  }
}

export async function signUp(input: SignUpInput): Promise<SignUpResult> {
  return platformApi<SignUpResult>('/api/platform/auth/sign-up/email', {
    method: 'POST',
    body: input,
  })
}

export interface SignInInput {
  email: string
  password: string
  /**
   * Optional redirect target. When present, and the user is
   * unverified, better-auth re-fires the verification email with
   * this URL embedded so the user lands here after clicking.
   */
  callbackURL?: string
}

export async function signIn(input: SignInInput) {
  return platformApi('/api/platform/auth/sign-in/email', {
    method: 'POST',
    body: input,
  })
}

export async function signOut() {
  return platformApi('/api/platform/auth/sign-out', {
    method: 'POST',
  })
}

// ---------- Password reset ----------
//
// Thin wrappers over better-auth's password-reset endpoints. The
// `requestPasswordReset` endpoint always returns 200 with a generic
// message regardless of whether the email exists — this is a
// deliberate account-enumeration mitigation and the client
// shouldn't try to defeat it by showing different copy per case.
//
// `resetPassword` takes the token from the URL query string that
// better-auth redirects to after the user clicks the email link.

export interface RequestPasswordResetInput {
  email: string
  /**
   * Where the user lands after clicking the email link. better-auth
   * redirects from its own `/reset-password/:token?callbackURL=…`
   * to `${redirectTo}?token=xyz`, so this is the public URL of
   * your reset-password page.
   */
  redirectTo: string
}

export async function requestPlatformPasswordReset(
  input: RequestPasswordResetInput,
): Promise<void> {
  await platformApi('/api/platform/auth/request-password-reset', {
    method: 'POST',
    body: input,
  })
}

export interface ResetPasswordInput {
  /** The token from the URL query string (?token=xyz). */
  token: string
  /** New password. Backend enforces the min length (8). */
  newPassword: string
}

export async function resetPlatformPassword(input: ResetPasswordInput): Promise<void> {
  // Token travels in both the query string AND the body — the
  // better-auth endpoint accepts either, and using the body keeps
  // it out of the URL the fetch logger prints.
  await platformApi('/api/platform/auth/reset-password', {
    method: 'POST',
    body: input,
  })
}

// ---------- SSO handoff ----------

/**
 * Ask the backend to mint a one-time pickup URL for hopping into a
 * tenant's ERP from the marketing app. The caller takes the returned
 * URL and assigns it to `window.location.href` — browser navigation
 * to the backend origin is what lets the tenant `cloud_session`
 * cookie get set before the ERP frontend loads.
 *
 * Requires a platform session. Requires the caller to be a member
 * of the target tenant (`tenant_members`). Returns 403 otherwise.
 */
export async function requestSsoHandoff(tenantId: string): Promise<{ url: string }> {
  return platformApi<{ url: string }>(`/api/platform/sso/handoff/${encodeURIComponent(tenantId)}`, {
    method: 'POST',
  })
}

/**
 * Admin-only cross-tenant SSO. Unlike `requestSsoHandoff`, this does
 * NOT require the caller to be a tenant member — platform_admin can
 * impersonate into ANY active tenant. The backend writes a
 * structured audit log line every time this is called.
 *
 * Returns the same `{url}` shape as `requestSsoHandoff`, so the
 * caller can do `window.location.href = url` verbatim.
 *
 * 403 from:
 *   - non-platform_admin caller
 *   - inactive tenant
 */
export async function impersonateTenant(
  tenantId: string,
  reason: string,
): Promise<{ url: string }> {
  return platformApi<{ url: string }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/impersonate`,
    { method: 'POST', body: { reason } },
  )
}

export interface TenantTimelineEvent {
  id: string
  occurredAt: string
  actorEmail: string | null
  action: string
  targetLabel: string | null
  metadata: Record<string, unknown>
  status: string
}

export interface TimelineParams {
  q?: string
  action?: string
  failedOnly?: boolean
  limit?: number
  offset?: number
}

export async function getTenantTimeline(
  tenantId: string,
  params: TimelineParams = {},
  signal?: AbortSignal,
): Promise<{ events: TenantTimelineEvent[]; total: number }> {
  const query = new URLSearchParams()
  if (params.q) query.set('q', params.q)
  if (params.action) query.set('action', params.action)
  if (params.failedOnly) query.set('status', 'failure')
  if (params.limit) query.set('limit', String(params.limit))
  if (params.offset) query.set('offset', String(params.offset))
  const suffix = query.size > 0 ? `?${query}` : ''
  return platformApi<{ events: TenantTimelineEvent[]; total: number }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/timeline${suffix}`,
    { signal },
  )
}

export interface TenantNote {
  id: string
  authorEmail: string
  body: string
  createdAt: string
}

export async function getTenantNotes(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantNote[]> {
  const data = await platformApi<{ notes: TenantNote[] }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/notes`,
    { signal },
  )
  return data.notes
}

export async function addTenantNote(tenantId: string, body: string): Promise<TenantNote> {
  const data = await platformApi<{ note: TenantNote }>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/notes`,
    { method: 'POST', body: { body } },
  )
  return data.note
}

export async function deleteTenantNote(tenantId: string, noteId: string): Promise<void> {
  await platformApi<void>(
    `/api/platform/admin/tenants/${encodeURIComponent(tenantId)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'DELETE' },
  )
}

// ---------- Admin: platform users ----------

/**
 * One row in the /admin/users listing. `tenantCount` is the number
 * of workspaces the user sees via `tenant_members` — useful for the
 * admin to spot power users / abandoned accounts at a glance.
 *
 * `role` is one of the four values declared on the backend; we type
 * it loosely as `string` because the admin UI has to render whatever
 * comes back (including values added later) without crashing.
 */
export interface AdminPlatformUser {
  id: string
  email: string
  name: string | null
  image: string | null
  role: string
  emailVerified: boolean
  createdAt: string
  updatedAt: string
  tenantCount: number
}

export async function getAdminUsers(signal?: AbortSignal): Promise<AdminPlatformUser[]> {
  const data = await platformApi<{ users: AdminPlatformUser[] }>('/api/platform/admin/users', {
    signal,
  })
  return data.users
}

export type PlatformRole =
  | 'platform_admin'
  | 'platform_support'
  | 'platform_readonly'
  | 'customer_owner'

/**
 * Update a platform user's role. Backend restricts this endpoint to
 * callers with role `platform_admin`; platform_support / readonly /
 * customer_owner get 403. Admins also cannot demote themselves — the
 * backend returns 403 with a descriptive message in that case.
 */
export async function updateAdminUser(
  userId: string,
  role: PlatformRole,
): Promise<AdminPlatformUser> {
  const data = await platformApi<{ user: AdminPlatformUser }>(
    `/api/platform/admin/users/${encodeURIComponent(userId)}`,
    {
      method: 'PATCH',
      body: { role },
    },
  )
  return data.user
}

// ---------- Admin: platform audit log ----------

/**
 * One row in the platform_audit table. Fields are denormalized
 * snapshots — actorEmail / actorRole reflect the user at the time
 * the action ran, not their current state. Metadata is free-form
 * JSON; the viewer renders it as a one-line summary inline and
 * (in a follow-up) a full JSON pane on row expansion.
 */
export interface AdminAuditEvent {
  id: string
  occurredAt: string
  actorKind: string
  actorUserId: string | null
  actorEmail: string | null
  actorRole: string | null
  actorIp: string | null
  action: string
  targetKind: string | null
  targetId: string | null
  targetLabel: string | null
  metadata: Record<string, unknown>
  status: string
  errorMessage: string | null
}

/**
 * Filters accepted by the audit list endpoint. All optional and
 * AND-combined server-side. Pass an empty object for the unfiltered
 * "newest first" view.
 */
export interface AuditFilters {
  action?: string
  actorEmail?: string
  targetKind?: string
  targetId?: string
  limit?: number
  offset?: number
}

export interface AdminAuditPage {
  events: AdminAuditEvent[]
  total: number
  page: { limit: number; offset: number }
}

export async function getAdminAuditEvents(
  filters: AuditFilters = {},
  signal?: AbortSignal,
): Promise<AdminAuditPage> {
  const params = new URLSearchParams()
  if (filters.action) params.set('action', filters.action)
  if (filters.actorEmail) params.set('actorEmail', filters.actorEmail)
  if (filters.targetKind) params.set('targetKind', filters.targetKind)
  if (filters.targetId) params.set('targetId', filters.targetId)
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = qs ? `/api/platform/admin/audit?${qs}` : '/api/platform/admin/audit'
  return platformApi<AdminAuditPage>(path, { signal })
}
